import type { PimsExportFormat } from "@hooks/use-pims-export-format";
import { stripNbsp } from "@src/utils";
import { getClipboardPayload } from "@src/utils/clipboard";
import { Table } from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import { Markdown } from "@tiptap/markdown";
import type { AnyExtension, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Marked } from "marked";
import type { RefObject } from "react";

/**
 * Dedicated `marked` instance for our markdown→HTML conversion.
 *
 * We must NOT use the global `marked` singleton because `@tiptap/markdown`
 * registers custom tokenizers (e.g. "underline") on it via `marked.use()`.
 * Those tokenizers produce token types that have no corresponding renderer
 * in the global instance (the renderers live inside TipTap's MarkdownManager).
 * Calling `marked.parse()` on content that triggers those tokens therefore
 * throws: "Token with 'underline' type was not found."
 *
 * A private instance is immune to that global pollution. We then register
 * only the extensions we need — with BOTH tokenizer and renderer — so custom
 * syntax like `++underline++` round-trips correctly through our pipeline.
 */
const localMarked = new Marked();

// Register the underline extension that mirrors @tiptap/extension-underline's
// markdownTokenizer. StarterKit v3.19+ bundles Underline, and TipTap's
// getMarkdown() serializes underlined text as `++text++`. We need both the
// tokenizer (to parse it) and the renderer (to emit <u> HTML) so the content
// survives a save→reload round-trip.
localMarked.use({
  extensions: [
    {
      name: "underline",
      level: "inline" as const,
      start(src: string) {
        return src.indexOf("++");
      },
      tokenizer(src: string) {
        const match = /^\+\+([\s\S]+?)\+\+/.exec(src);
        if (!match) return undefined;
        return {
          type: "underline",
          raw: match[0],
          text: match[1].trim(),
          tokens: this.lexer.inlineTokens(match[1].trim()),
        };
      },
      childTokens: ["tokens"],
      renderer(token) {
        return `<u>${this.parser.parseInline(token.tokens ?? [])}</u>`;
      },
    },
  ],
});

/**
 * Convert a plain-text string (single \n between paragraphs) into
 * TipTap-compatible HTML. Each line becomes a <p>; empty lines become
 * empty <p></p> paragraphs. Markdown syntax characters are preserved as
 * literal text (escaped).
 */
export function textToHtml(text: string): string {
  if (!text) return "";
  return stripNbsp(text)
    .split("\n")
    .map(
      (line: string) =>
        `<p>${line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`,
    )
    .join("");
}

/**
 * Convert a markdown string (stored with single \n between paragraphs)
 * into TipTap-compatible HTML, preserving blank lines as empty <p></p>
 * paragraphs.
 *
 * The standard markdown parser collapses multiple blank lines into one
 * paragraph break. This function splits at blank-line boundaries first,
 * converts each content segment via `marked`, then re-inserts empty
 * paragraphs for each blank line.
 */
export function markdownToHtmlPreservingBlanks(text: string): string {
  if (!text) return "";
  const cleaned = stripNbsp(text);

  // Split into content segments and blank-line separators.
  // In our \n storage format: \n = paragraph break, \n\n+ = blank lines.
  const parts = cleaned.split(/(\n{2,})/);

  let html = "";
  for (const part of parts) {
    if (/^\n{2,}$/.test(part)) {
      // Each newline beyond the first represents an empty paragraph
      const emptyCount = part.length - 1;
      html += "<p></p>".repeat(emptyCount);
    } else if (part) {
      // Content segment: expand single \n to \n\n for the markdown parser
      // (markdown uses \n\n as paragraph break)
      const mdText = part.replace(/\n/g, "\n\n");
      html += localMarked.parse(mdText, { async: false }) as string;
    }
  }

  return html;
}

/**
 * Strip `&nbsp;` markers that getMarkdown() inserts for empty paragraphs.
 * These appear as `&nbsp;` alone on a line (between `\n\n` boundaries).
 */
export function stripMarkdownEmptyParagraphs(text: string): string {
  return text.replace(/^&nbsp;$/gm, "");
}

/**
 * Halve every run of 2+ newlines produced by getMarkdown(), converting
 * from markdown's \n\n paragraph breaks back to our single \n format.
 * Odd counts round up (e.g. 3 → 2) to preserve extra visual breaks.
 */
export function collapseMarkdownNewlines(text: string): string {
  return text.replace(/\n{2,}/g, (m) =>
    "\n".repeat(Math.ceil(m.length / 2)),
  );
}

/**
 * Serialize the ProseMirror document slice between `from` and `to` back to
 * markdown, applying the same post-processing used throughout the editor.
 * Falls back to plain text extraction if the markdown serializer is not
 * available (e.g. before the editor's `onCreate` fires).
 */
export function setClipboardFromSelection(
  view: Editor["view"],
  event: ClipboardEvent,
  editorRef: RefObject<Editor | null>,
  from: number,
  to: number,
  format: PimsExportFormat,
) {
  const text = serializeSelectionAsMarkdown(
    view,
    editorRef,
    from,
    to,
  );
  const { plain, html } = getClipboardPayload(text, format);
  event.clipboardData?.setData("text/plain", plain);
  event.clipboardData?.setData("text/html", html);
}

const PLAIN_TEXT_STARTER_KIT_CONFIG = {
  heading: false,
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  code: false,
  codeBlock: false,
  blockquote: false,
  bulletList: false,
  orderedList: false,
  listItem: false,
  horizontalRule: false,
} as const;

/**
 * Returns the TipTap extensions array for the given mode, optionally
 * appending extra extensions (e.g. Placeholder, input rules) for editable use.
 */

export function buildEditorExtensions(
  plainText: boolean,
  extraExtensions: AnyExtension[] = [],
): AnyExtension[] {
  return [
    StarterKit.configure(
      plainText
        ? PLAIN_TEXT_STARTER_KIT_CONFIG
        : { heading: { levels: [1, 2, 3] } },
    ),
    ...(plainText
      ? []
      : [Table, TableRow, TableHeader, TableCell, Markdown]),
    ...extraExtensions,
  ];
}

/**
 * Converts stored content to TipTap-compatible HTML for the given mode.
 * Plain text escapes markdown syntax; rich text renders it via marked.
 */
export function contentToEditorHtml(
  content: string,
  plainText: boolean,
): string {
  return plainText
    ? textToHtml(content)
    : markdownToHtmlPreservingBlanks(content);
}

export function serializeSelectionAsMarkdown(
  view: Editor["view"],
  editorRef: RefObject<Editor | null>,
  from: number,
  to: number,
): string {
  const slice = view.state.doc.slice(from, to);
  const tempDoc = view.state.schema.topNodeType.create(
    null,
    slice.content,
  );
  const raw =
    editorRef.current?.storage?.markdown?.manager?.serialize(
      tempDoc.toJSON(),
    ) ?? view.state.doc.textBetween(from, to, "\n");
  return collapseMarkdownNewlines(
    stripNbsp(stripMarkdownEmptyParagraphs(raw)),
  );
}
